import castArray from 'lodash/castArray.js';
import every from 'lodash/every.js';
import has from 'lodash/has.js';

import Permission from '@/models/Permission.js';
import Gate from '@/plugins/vue-gate/Gate.js';
import GateAccessDenied from '@/plugins/vue-gate/GateAccessDenied.js';
import GateKeeper from '@/plugins/vue-gate/GateKeeper.js';

/**
 * Whether the gate keeper's user holds the permission a route `{ action, subject }` ability names.
 * Shared with navigation so a link is only shown when its route would let the user in.
 * @param {GateKeeper} gateKeeper
 * @param {{ action: string, subject: string }} ability
 * @returns {boolean}
 */
export function gateKeeperAllowsAbility(gateKeeper, ability) {
    if (!has(ability, 'action') || !has(ability, 'subject')) {
        return false;
    }

    const { action, subject } = ability;

    const permission = Permission.query().where('action', action).where('subject', subject).first();

    return Boolean(
        permission &&
            (gateKeeper.userHasPermission(permission.name) || gateKeeper.user.hasGroupPermissionTo(permission.name))
    );
}

export default class GateKeeperMiddleware {
    /**
     * @param store
     * @param router
     */
    constructor(store, router) {
        this.store = store;
        this.router = router;

        this.user = this.store.getters['Auth/user'];

        this.gate = new Gate(store).forUser(this.user);

        this.gateKeeper = new GateKeeper().forUser(this.user);
        this.gateKeeper.refreshUser = async () => {
            if (typeof this.store.dispatch !== 'function') {
                return;
            }

            await this.store.dispatch('Auth/fetchUser', true);

            const user = this.store.getters['Auth/user'];

            if (user) {
                this.user = user;
                this.gate.forUser(user);
                this.gateKeeper.forUser(user);
            }
        };
    }

    async fire(to, from, abilities) {
        let gateResponse = false;

        if (typeof abilities === 'function') {
            gateResponse = await abilities({ gateKeeper: this.gateKeeper, gate: this.gate, to, from });
        } else {
            const allAbilities = castArray(abilities);

            gateResponse = every(allAbilities, ability => gateKeeperAllowsAbility(this.gateKeeper, ability));
        }

        if (!gateResponse) {
            throw new GateAccessDenied(this.gateKeeper.denialMessage(to));
        }

        return true;
    }
}
